// ABOUTME: Quick To-do state. Guests keep a localStorage list; signed-in users sync
// ABOUTME: through users/{uid}/widgets/quickTodo (the SDK's persistent cache paints it instantly).

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { todoService, type TodoItem } from "../services/todoService";

/**
 * The guest list, and the only key the widget used before it synced — so for
 * an existing user it also holds their pre-sync tasks, which the first
 * sign-in uploads (see the `null` branch below).
 */
export const GUEST_TODO_KEY = "quick_todo_items";

function readList(key: string): TodoItem[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TodoItem[]) : [];
  } catch {
    return [];
  }
}

function writeList(key: string, todos: TodoItem[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(todos));
  } catch {
    // localStorage may be full or unavailable
  }
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently ignore
  }
}

export function useQuickTodo(userId: string | undefined) {
  const [todos, setTodosState] = useState<TodoItem[]>(() =>
    userId ? [] : readList(GUEST_TODO_KEY)
  );
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  // Updates are computed against the latest list synchronously so two quick
  // clicks don't each write a stale copy.
  const todosRef = useRef(todos);

  const apply = useCallback((next: TodoItem[]) => {
    todosRef.current = next;
    setTodosState(next);
  }, []);

  // A different user (or none) starts from their own list: the guest list
  // from localStorage, or empty until the account's snapshot arrives. Reset
  // while rendering so no frame shows the previous user's list.
  const [subscribedUserId, setSubscribedUserId] = useState(userId);
  if (userId !== subscribedUserId) {
    setSubscribedUserId(userId);
    setTodosState(userId ? [] : readList(GUEST_TODO_KEY));
    setLoadError(null);
  }
  useLayoutEffect(() => {
    todosRef.current = todos;
  });

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = todoService.subscribe(
      userId,
      (remote) => {
        if (remote === null) {
          // No server copy yet. Seed it from the pre-sync local list so the
          // upgrade from local-only storage loses nothing.
          const legacy = readList(GUEST_TODO_KEY);
          apply(legacy);
          if (legacy.length > 0) {
            todoService
              .save(userId, legacy)
              .then(() => removeKey(GUEST_TODO_KEY))
              .catch((err) =>
                setSaveError(err instanceof Error ? err : new Error("Failed to save tasks"))
              );
          }
          return;
        }
        apply(remote);
      },
      (err) => setLoadError(err)
    );

    return unsubscribe;
  }, [userId, apply]);

  const update = useCallback(
    (fn: (prev: TodoItem[]) => TodoItem[]) => {
      const next = fn(todosRef.current);
      apply(next);
      if (!userId) {
        writeList(GUEST_TODO_KEY, next);
        return;
      }
      todoService
        .save(userId, next)
        .then(() => setSaveError(null))
        .catch((err) =>
          setSaveError(err instanceof Error ? err : new Error("Failed to save tasks"))
        );
    },
    [userId, apply]
  );

  return { todos, update, saveError, loadError };
}
